import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import axios from "axios";
import CollectionsIcon from "@mui/icons-material/Collections";
import NavbarCharity from "../layout/NavbarCharity";
import { Title } from "../components/charity/title";
import FooterCharity from "../layout/FooterCharity";
import AlbumLightbox from "../components/AlbumLightbox";
import { isVideoFile, isYouTubeUrl, parseYouTubeId } from "../utils/mediaUtils";

// 每頁相簿數（桌機三欄 × 四列）
const PAGE_SIZE = 12;

// 相簿名稱開頭的日期（例：2025.02.09、2026.04.22-04.26）拆出來另外顯示
const DATE_PREFIX = /^(\d{4}\.\d{1,2}\.\d{1,2}(?:\s*[-~]\s*(?:\d{1,2}\.)?\d{1,2})?)\s*(.*)$/;
function splitName(name = "") {
  const m = name.match(DATE_PREFIX);
  return m && m[2] ? { date: m[1], text: m[2] } : { date: "", text: name };
}

// 相簿說明（後台常把說明填成跟名稱一樣，重複就不顯示）
const descOf = (album) => {
  const d = (album.title || "").trim();
  return d && d !== (album.name || "").trim() ? d : "";
};

// 封面縮圖：一般圖片直接用；YouTube 用官方縮圖；影片檔用 <video> 第一格
function Cover({ src, alt }) {
  const cls = "absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]";
  if (!src) {
    return <div className="absolute inset-0 bg-gradient-to-br from-[#a93230] to-[#6e1f1e]" />;
  }
  if (isYouTubeUrl(src)) {
    const id = parseYouTubeId(src);
    return <img src={`https://img.youtube.com/vi/${id}/hqdefault.jpg`} alt={alt} className={cls} loading="lazy" decoding="async" />;
  }
  if (isVideoFile(src)) {
    return <video src={src} className={cls} muted playsInline preload="metadata" />;
  }
  return <img src={src} alt={alt} className={cls} loading="lazy" decoding="async" />;
}

Cover.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
};

// 頁碼：1 … 4 5 6 … 10
function pageItems(page, totalPages) {
  const set = new Set([1, totalPages, page - 1, page, page + 1]);
  const pages = [...set].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  const out = [];
  pages.forEach((p, i) => {
    if (i > 0 && p - pages[i - 1] > 1) out.push("…" + p);
    out.push(p);
  });
  return out;
}

function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  const btn =
    "min-w-[2.5rem] h-10 px-3 rounded-full text-base transition-colors disabled:cursor-not-allowed disabled:opacity-30";
  return (
    <nav className="flex flex-wrap items-center justify-center gap-2 py-12 mobile:gap-1 mobile:py-8" aria-label="分頁">
      <button
        type="button"
        className={`${btn} hover:bg-[#a93230]/10`}
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
        aria-label="上一頁"
      >
        ‹
      </button>
      {pageItems(page, totalPages).map((p) =>
        typeof p === "string" ? (
          <span key={p} className="px-1 text-gray-400">…</span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            aria-current={p === page ? "page" : undefined}
            className={`${btn} ${p === page ? "bg-[#a93230] text-white" : "hover:bg-[#a93230]/10"}`}
          >
            {p}
          </button>
        )
      )}
      <button
        type="button"
        className={`${btn} hover:bg-[#a93230]/10`}
        onClick={() => onChange(page + 1)}
        disabled={page >= totalPages}
        aria-label="下一頁"
      >
        ›
      </button>
    </nav>
  );
}

Pagination.propTypes = {
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

function Charity3() {
  const [searchParams, setSearchParams] = useSearchParams();
  const charityID = Number(searchParams.get("id")) || 0;
  const order = searchParams.get("order") || "";
  const page = Math.max(1, Number(searchParams.get("page")) || 1);

  const [charity, setCharity] = useState({});
  const [allAlbums, setAllAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const listTopRef = useRef(null);

  // 燈箱：開啟中的相簿與目前張
  const [openAlbum, setOpenAlbum] = useState(null);
  const [photoIndex, setPhotoIndex] = useState(0);

  // 沿用原本 API 一次取回全部相簿，前端自行分頁；
  // 畫面上只渲染當頁的相簿封面，相簿內其他照片於打開燈箱時才載入。
  useEffect(() => {
    if (!charityID) return undefined;
    let cancelled = false;
    setLoading(true);
    axios
      .post("/api/website/getCharityAlbum", { id: charityID })
      .then((response) => {
        if (cancelled) return;
        const { data } = response.data;
        document.title = data.data.title;
        setCharity(data.data);
        setAllAlbums(Array.isArray(data.albums) ? data.albums : []);
      })
      .catch((error) => {
        if (!cancelled) console.error("Error fetching charity albums:", error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [charityID]);

  // 依後台排序欄位排序（預設升冪；order=desc / orderDesc 為降冪），並補上封面與張數
  const sortedAlbums = useMemo(() => {
    const normalized = order.toLowerCase();
    const isDesc = normalized === "desc" || normalized === "orderdesc";
    const list = [...allAlbums].sort((a, b) => (Number(a?.order) || 0) - (Number(b?.order) || 0));
    if (isDesc) list.reverse();
    return list.map((a) => {
      const contentList = Array.isArray(a.contentList) ? a.contentList : [];
      return {
        ...a,
        contentList,
        cover: a.image || contentList[0]?.image || "",
        count: contentList.length,
      };
    });
  }, [allAlbums, order]);

  const total = sortedAlbums.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const curPage = Math.min(page, totalPages); // 網址頁碼超出範圍時退回最後一頁
  const albums = sortedAlbums.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);

  const goPage = (p) => {
    const next = new URLSearchParams(searchParams);
    if (p <= 1) next.delete("page");
    else next.set("page", String(p));
    setSearchParams(next);
    listTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openAlbumAt = (album) => {
    setOpenAlbum(album);
    setPhotoIndex(0);
  };

  const closeAlbum = useCallback(() => setOpenAlbum(null), []);

  return (
    <>
      <NavbarCharity path={charityID ? String(charityID) : ""} charityName={charity.title} />
      <main className="relative">
        <section className="h-[250px] m-auto page-title flex items-center justify-center">
          <Title title={"活動紀錄"} subTitle={"Activity Record"} />
        </section>

        <section
          ref={listTopRef}
          className="max-w-[1300px] mx-auto w-[90%] scroll-mt-24 pt-4 mobile:max-w-[500px] mobile:w-[85%]"
        >
          {loading && albums.length === 0 ? (
            <ul className="grid grid-cols-3 gap-8 table:grid-cols-2 table:gap-6 mobile:grid-cols-1 mobile:gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <li key={i} className="overflow-hidden rounded-xl bg-white shadow-md">
                  <div className="aspect-[4/3] animate-pulse bg-gray-200" />
                  <div className="space-y-3 p-6">
                    <div className="h-4 w-1/3 animate-pulse rounded bg-gray-200" />
                    <div className="h-6 w-4/5 animate-pulse rounded bg-gray-200" />
                    <div className="h-4 w-full animate-pulse rounded bg-gray-100" />
                  </div>
                </li>
              ))}
            </ul>
          ) : albums.length === 0 ? (
            <p className="py-20 text-center text-lg text-gray-500">目前尚無活動紀錄。</p>
          ) : (
            <ul
              className={`grid grid-cols-3 gap-8 transition-opacity table:grid-cols-2 table:gap-6 mobile:grid-cols-1 mobile:gap-6 ${
                loading ? "opacity-50" : ""
              }`}
            >
              {albums.map((album) => {
                const { date, text } = splitName(album.name);
                return (
                  <li key={album.id}>
                    <button
                      type="button"
                      onClick={() => openAlbumAt(album)}
                      aria-label={`查看相簿：${album.name}`}
                      className="group relative flex h-full w-full cursor-zoom-in flex-col overflow-hidden rounded-xl bg-white text-left shadow-[0_8px_24px_-12px_rgba(0,0,0,0.25)] ring-1 ring-black/[0.06] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_-12px_rgba(169,50,48,0.35)] hover:ring-[#a93230]/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#a93230]"
                    >
                      <div className="relative aspect-[4/3] w-full overflow-hidden bg-gray-100">
                        <Cover src={album.cover} alt={album.name} />
                        {album.count > 1 && (
                          <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-xs font-semibold text-white">
                            <CollectionsIcon sx={{ fontSize: 14 }} aria-hidden />
                            {album.count}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 p-6 mobile:p-5">
                        {date && (
                          <p className="mb-2 text-sm tracking-[0.15em] text-gray-500">{date}</p>
                        )}
                        <h3 className="mb-3 text-xl font-normal leading-snug text-[#a93230] mobile:text-lg">
                          {text}
                        </h3>
                        {descOf(album) && (
                          <p className="line-clamp-3 text-base leading-relaxed text-gray-700">
                            {descOf(album)}
                          </p>
                        )}
                      </div>
                      <div
                        aria-hidden
                        className="absolute inset-x-0 bottom-0 h-[3px] bg-gradient-to-r from-transparent via-[#a93230] to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <Pagination page={curPage} totalPages={totalPages} onChange={goPage} />
        </section>

        <AlbumLightbox
          open={!!openAlbum}
          items={openAlbum ? openAlbum.contentList : []}
          index={photoIndex}
          onIndexChange={setPhotoIndex}
          onClose={closeAlbum}
          title={openAlbum?.name}
          description={openAlbum ? descOf(openAlbum) : undefined}
        />
      </main>
      <FooterCharity />
    </>
  );
}

export default Charity3;
